import React, { useState, useRef, useEffect, useCallback } from "react";
import { PropertyRow, isLockedProperty } from "../types";
import { searchProperties } from "../api";
import LeadTypeMultiSelect from "./LeadTypeMultiSelect";
import OwnerTypeMultiSelect from "./OwnerTypeMultiSelect";
import MapBuyBoxFields, { MapBuyBoxSavedConfig, MapBuyBoxStats } from "./MapBuyBoxFields";
import { MapBuyBox, mapBuyBoxActiveCount } from "../lib/mapBuyBox";
import { exportErrorMessage } from "../lib/exportErrors";
import { USER_VISIBLE_LEAD_SIGNALS, LeadSignalType } from "../lib/leadTypeMeta";
import { resolveDisplayParcelId } from "../lib/dataQuality";

interface SearchAutocompleteProps {
  /**
   * The currently-selected county. Used to (a) tag autocomplete results
   * that belong to a *different* county with a county pill, so the user
   * can see they'll be jumped across counties on click, and (b) decide
   * whether to invoke the cross-county switch path on selection.
   */
  currentCountyId: string;

  onSearchChange: (query: string) => void;

  /**
   * Selection handler. Implementations should detect when the result's
   * countyId differs from currentCountyId and switch the active county
   * BEFORE opening the modal, so map.tsx flies to the right viewport.
   * See App.tsx handleCrossCountySelect.
   *
   * Takes PropertyRow, not Property (#2378) — search can return a locked
   * teaser for a result outside the viewer's subset; the implementation
   * must narrow with isLockedProperty, same as every other selection path.
   */
  onSelectProperty: (property: PropertyRow) => void;

  // Filter-row props (unchanged — these operate over the in-memory
  // properties list for the active county).
  statusFilter: string;
  onStatusFilterChange: (status: string) => void;
  propertyClassFilter: string;
  onPropertyClassFilterChange: (c: string) => void;

  // "" = All, "auction" = live auctions only, "lead" = pre-auction leads
  // only. leadTypeFilter narrows leads further by signal type.
  inAuctionFilter: "" | "auction" | "lead";
  onInAuctionFilterChange: (v: "" | "auction" | "lead") => void;
  // Checked lead types (any of them matches); empty = all.
  leadTypeFilter: string[];
  onLeadTypeFilterChange: (t: string[]) => void;
  // Owner-type checkboxes (#3205): any checked type matches. Optional: omitted,
  // the control does not render.
  ownerTypeFilter?: string[];
  onOwnerTypeFilterChange?: (t: string[]) => void;

  /**
   * Priority-ordered signal types actually present in the currently-selected
   * county (#2257) — computed by the parent (leadTypeMeta.ts's
   * computeAvailableSignalTypes) from the county's already-fetched
   * properties, so a county with no HOA/code-enforcement leads doesn't offer
   * those as filter options. Optional: falls back to the full
   * USER_VISIBLE_LEAD_SIGNALS list so callers/tests that don't thread this
   * through keep the pre-#2257 behavior instead of rendering an empty menu.
   */
  availableLeadTypes?: LeadSignalType[];

  // Vacant-land-only toggle (#1604). Independent of propertyClassFilter's
  // "Land Only" option — that one reads property.propertyClass, which is
  // only reliably populated in Mecklenburg/Guilford; this one reads the
  // broader isVacantLand() heuristic and works across every county.
  //
  // Lives behind the "More" popover, not the visible bar (2026-09 tab-chrome
  // redesign) — it's a growth valve: as more discovery filters accumulate
  // (min/max value, sale-date range, acreage...) they land here instead of
  // stretching the bar. Sort and the result count moved OFF this bar
  // entirely — they now live on PropertyList's list header, next to the
  // thing they actually describe (see PropertyList.tsx's sortKey props).
  vacantOnly: boolean;
  onVacantOnlyChange: (v: boolean) => void;

  // Map buy box (#3061), also in the More popover. Optional: omitted, the
  // fields don't render.
  buyBox?: MapBuyBox;
  onBuyBoxChange?: (next: MapBuyBox) => void;
  buyBoxMatchCount?: number;
  buyBoxStats?: MapBuyBoxStats;
  /** Saved buy boxes in the popover (paid plans); omit to hide. */
  buyBoxSaved?: MapBuyBoxSavedConfig;

  // Whole-county CSV export (paid tiers). Resolves once the file has been
  // handed to the browser and rejects on failure; this component owns the
  // busy/error/menu-open state so callers only supply the download itself.
  // `addedWithinDays` is null for the full feed, or N to keep only rows
  // added in the last N days. Omit to hide the control (free tier —
  // Dashboard also only renders this whole component in the "all
  // properties" view, so watchlist never sees it either).
  onExportCsv?: (addedWithinDays: number | null) => Promise<void>;
}

// Export menu options, in display order. `days: null` is the unfiltered feed.
const EXPORT_RANGES: { label: string; days: number | null }[] = [
  { label: "Whole county", days: null },
  { label: "Added last 24 hours", days: 1 },
  { label: "Added last 7 days", days: 7 },
  { label: "Added last 30 days", days: 30 },
];

// NOTE: a third status-label registry, independent of lib/statusMeta.ts and of
// the <option> list below. It is deliberately a SUBSET — this dropdown has never
// offered notice_of_default, postponed, withdrawn or delisted — so it is left
// hand-maintained here rather than derived. Adding code_violation only (ADR
// 0037); widening it to the full vocabulary is a separate UX decision.
const statusLabels: Record<string, string> = {
  notice_filed: "Notice Filed",
  auction_scheduled: "Auction Scheduled",
  auction_passed: "Auction Passed",
  case_concluded: "Case Concluded",
  pending_confirmation: "Pending Confirmation",
  res_plus: "Res Plus",
  sold: "Sold",
  redeemed: "Redeemed",
  code_violation: "Code Violation",
};

// Render a friendly short county tag from the slug. "cabarrus-nc" →
// "CABARRUS". Used as the small pill on autocomplete rows. We don't
// need the full County object here; the slug-derived form is enough
// for an at-a-glance signal.
function countyShortLabel(countyId: string): string {
  return countyId.replace(/-nc$/i, "").toUpperCase();
}

const DEBOUNCE_MS = 300;
const MAX_SUGGESTIONS = 8;
// The backend's trigram index can't narrow a 1-2 character substring, so
// SearchByText returns nothing below 3 characters (searchMinQueryRunes in
// property_repository.go). Don't spend a request (or a rate-limit token) on it.
const MIN_QUERY_CHARS = 3;

const SearchAutocomplete: React.FC<SearchAutocompleteProps> = ({
  currentCountyId,
  onSearchChange,
  onSelectProperty,
  statusFilter,
  onStatusFilterChange,
  propertyClassFilter,
  onPropertyClassFilterChange,
  inAuctionFilter,
  onInAuctionFilterChange,
  leadTypeFilter,
  onLeadTypeFilterChange,
  ownerTypeFilter,
  onOwnerTypeFilterChange,
  vacantOnly,
  onVacantOnlyChange,
  buyBox,
  onBuyBoxChange,
  buyBoxMatchCount = 0,
  buyBoxStats = { missingKept: 0, missingExcluded: 0, outsideExcluded: 0 },
  buyBoxSaved,
  availableLeadTypes = USER_VISIBLE_LEAD_SIGNALS,
  onExportCsv,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [suggestions, setSuggestions] = useState<PropertyRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const moreRef = useRef<HTMLDivElement>(null);
  const exportRef = useRef<HTMLDivElement>(null);

  // "More" popover — the growth valve for filters that don't earn a
  // permanent slot on the visible bar (today: property class, vacant lots
  // only). The badge count generalizes cleanly as more join it.
  const moreActiveCount =
    (propertyClassFilter ? 1 : 0) + (vacantOnly ? 1 : 0) + (buyBox ? mapBuyBoxActiveCount(buyBox) : 0);

  // Close the More popover on outside click — same pattern as the
  // autocomplete dropdown's outside-click handler below, kept separate
  // since the two popovers close independently.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        setMoreOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Close the Export menu on outside click, same pattern as More.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (exportRef.current && !exportRef.current.contains(e.target as Node)) {
        setExportOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Closes the menu on success. On failure, re-opens it (in case a prior
  // click had already closed it) so the error and the same options are
  // right there to retry, without the user re-finding the Export button.
  const handleExportClick = async (days: number | null) => {
    if (!onExportCsv || exporting) return;
    setExporting(true);
    setExportError(null);
    try {
      await onExportCsv(days);
      setExportOpen(false);
    } catch (err) {
      setExportError(exportErrorMessage(err, "Export failed. Please try again."));
      setExportOpen(true);
    } finally {
      setExporting(false);
    }
  };

  // Debounced search effect. When the input value changes, wait
  // DEBOUNCE_MS before firing the backend call. Empty / whitespace
  // input clears suggestions without hitting the network.
  //
  // Cross-county semantics (#244): we intentionally pass NO countyId
  // to searchProperties() so the backend matches across all active
  // counties. Each result carries its own countyId; the dropdown
  // tags out-of-county rows with a small pill.
  useEffect(() => {
    const trimmed = inputValue.trim();
    if (trimmed.length < MIN_QUERY_CHARS) {
      setSuggestions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        // Global search — undefined countyId means "all active counties".
        const results = await searchProperties(trimmed, undefined, MAX_SUGGESTIONS);
        if (!cancelled) {
          setSuggestions(results.slice(0, MAX_SUGGESTIONS));
          setLoading(false);
        }
      } catch {
        // Network / 5xx — fall back to no suggestions rather than
        // breaking the input. The user can still hit Enter to commit
        // their literal query into the page filter.
        if (!cancelled) {
          setSuggestions([]);
          setLoading(false);
        }
      }
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [inputValue]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const commitSelection = useCallback(
    (property: PropertyRow) => {
      // A locked row has no address/parcelId to put in the box (#2378) —
      // fall back to city/state, which is all LockedProperty carries.
      // Never fall back to the raw parcelId when it's our synthetic
      // CASE:{court_file_number} placeholder (#687 / ADR 0023) — that
      // would drop the internal id into the visible search box as if it
      // were a real address or parcel number.
      if (isLockedProperty(property)) {
        setInputValue([property.city, property.state].filter(Boolean).join(", "));
      } else {
        setInputValue(property.address || resolveDisplayParcelId(property).value || "");
      }
      setIsOpen(false);
      setActiveIndex(-1);
      // Don't push the search-query string to the page filter — the
      // selection is itself the resolution. Empty the search filter so
      // the list view doesn't get filtered down to nothing in the
      // newly-switched county.
      onSearchChange("");
      onSelectProperty(property);
    },
    [onSearchChange, onSelectProperty]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);
    setActiveIndex(-1);
    setIsOpen(val.length > 0);
    if (val === "") onSearchChange("");
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || suggestions.length === 0) {
      if (e.key === "Enter") {
        onSearchChange(inputValue);
        setIsOpen(false);
      }
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0) {
        commitSelection(suggestions[activeIndex]);
      } else {
        onSearchChange(inputValue);
        setIsOpen(false);
      }
    } else if (e.key === "Escape") {
      setIsOpen(false);
    } else if (e.key === "Tab") {
      setIsOpen(false);
    }
  };

  const handleClear = () => {
    setInputValue("");
    setIsOpen(false);
    setActiveIndex(-1);
    setSuggestions([]);
    onSearchChange("");
    inputRef.current?.focus();
  };

  return (
    <div className="search-bar" aria-label="Parcely — NC off-market property map">
      <div className="search-bar-inner">
        {/* The "Parcely" brand strip above (.app-brandbar) is the single brand
            mark; the old "Foreclosure Map" h1 here was redundant and ate a
            row on mobile. Removed — region is labeled via aria-label. */}

        <div className="search-autocomplete" data-tour="search" ref={containerRef}>
          <div className="search-input-wrap">
            <input
              ref={inputRef}
              type="text"
              className="search-input"
              placeholder="Search any county — address, parcel ID, or court file..."
              // #211 mechanical a11y slice: screen readers cannot infer a name
              // from a placeholder alone (WCAG 4.1.2). aria-label mirrors the
              // placeholder intent so the input has a programmatic name
              // without adding a visible <label>.
              aria-label="Search Parcely by address, parcel ID, or court file"
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={() => inputValue.length > 0 && setIsOpen(true)}
              autoComplete="off"
            />
            {inputValue && (
              <button className="search-clear-btn" onClick={handleClear} aria-label="Clear">
                ×
              </button>
            )}
          </div>

          {isOpen && (
            <ul className="autocomplete-list" role="listbox">
              {loading && suggestions.length === 0 && (
                <li className="autocomplete-empty">Searching…</li>
              )}
              {inputValue.trim().length > 0 && inputValue.trim().length < MIN_QUERY_CHARS && (
                <li className="autocomplete-empty">
                  Keep typing — {MIN_QUERY_CHARS}+ characters to search.
                </li>
              )}
              {!loading && suggestions.length === 0 && inputValue.trim().length >= MIN_QUERY_CHARS && (
                <li className="autocomplete-empty">
                  No matches in any active county.
                </li>
              )}
              {suggestions.map((prop, i) => {
                const isOtherCounty = prop.countyId !== currentCountyId;
                // Locked rows (#2378) carry no address/parcelId — the old
                // unconditional fallback rendered these as the literal
                // string "Unknown Address", which reads as broken rather
                // than as the upsell teaser it's meant to be. Render city/
                // state/value-bucket instead, same info LockedPropertyCard
                // shows elsewhere, so search keeps proving the data depth
                // is real without pretending a locked row is a dead end.
                if (isLockedProperty(prop)) {
                  return (
                    <li
                      key={prop.id}
                      role="option"
                      aria-selected={i === activeIndex}
                      className={`autocomplete-item autocomplete-item--locked${i === activeIndex ? " autocomplete-item--active" : ""}`}
                      onMouseDown={() => commitSelection(prop)}
                    >
                      <span className="autocomplete-row">
                        <span className="autocomplete-address">
                          🔒 {[prop.city, prop.state].filter(Boolean).join(", ") || "Property"}
                        </span>
                        <span
                          className="autocomplete-county-pill"
                          title={`In ${prop.countyId} — outside your plan's coverage`}
                        >
                          {countyShortLabel(prop.countyId)}
                        </span>
                      </span>
                      <span className="autocomplete-meta">
                        {[
                          prop.valueBucket,
                          prop.status ? statusLabels[prop.status] || prop.status : "",
                          "Upgrade to see address",
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </li>
                  );
                }
                return (
                  <li
                    key={prop.id}
                    role="option"
                    aria-selected={i === activeIndex}
                    className={`autocomplete-item${i === activeIndex ? " autocomplete-item--active" : ""}`}
                    onMouseDown={() => commitSelection(prop)}
                  >
                    <span className="autocomplete-row">
                      <span className="autocomplete-address">
                        {prop.address || resolveDisplayParcelId(prop).value || "No address on file"}
                      </span>
                      {isOtherCounty && (
                        <span
                          className="autocomplete-county-pill"
                          title={`In ${prop.countyId} — selecting will switch counties`}
                        >
                          {countyShortLabel(prop.countyId)}
                        </span>
                      )}
                    </span>
                    <span className="autocomplete-meta">
                      {[
                        resolveDisplayParcelId(prop).value,
                        prop.status ? statusLabels[prop.status] || prop.status : "",
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="filter-row">
          <select
            value={statusFilter}
            onChange={(e) => onStatusFilterChange(e.target.value)}
            title="Filter by foreclosure status"
            aria-label="Filter by foreclosure status"
          >
            <option value="">All Statuses</option>
            <option value="notice_filed">Notice Filed</option>
            <option value="auction_scheduled">Auction Scheduled</option>
            <option value="auction_passed">Auction Passed</option>
            <option value="pending_confirmation">Pending Confirmation</option>
            <option value="sold">Sold</option>
            <option value="redeemed">Redeemed</option>
            <option value="code_violation">Code Violation</option>
          </select>

          <select
            value={inAuctionFilter}
            onChange={(e) => onInAuctionFilterChange(e.target.value as "" | "auction" | "lead")}
            title="Filter by auction vs. lead"
            aria-label="Filter by auction vs. lead"
          >
            <option value="">Auctions + Leads</option>
            <option value="auction">Auctions Only</option>
            <option value="lead">Leads Only</option>
          </select>

          {inAuctionFilter !== "auction" && (
            <LeadTypeMultiSelect
              value={leadTypeFilter}
              onChange={onLeadTypeFilterChange}
              options={availableLeadTypes}
            />
          )}

          {ownerTypeFilter && onOwnerTypeFilterChange && (
            <OwnerTypeMultiSelect value={ownerTypeFilter} onChange={onOwnerTypeFilterChange} />
          )}

          {/* "More" — growth valve for filters that don't earn a permanent
              slot on the visible bar (today: property class, vacant lots
              only). Sort and the result count live on PropertyList's list
              header now, next to the list they actually describe. */}
          <div className="filter-more" ref={moreRef}>
            <button
              type="button"
              className="filter-more-btn"
              aria-expanded={moreOpen}
              aria-haspopup="true"
              onClick={() => setMoreOpen((o) => !o)}
            >
              More
              {moreActiveCount > 0 && (
                <span className="filter-more-badge">{moreActiveCount}</span>
              )}
            </button>
            {moreOpen && (
              <div className="filter-more-popover" role="menu">
                <select
                  value={propertyClassFilter}
                  onChange={(e) => onPropertyClassFilterChange(e.target.value)}
                  title="Filter by property class"
                  aria-label="Filter by property class"
                >
                  <option value="">All Classes</option>
                  <option value="land_only">Land Only</option>
                  <option value="residential">Residential</option>
                  <option value="commercial">Commercial</option>
                </select>
                <label className="filter-row-checkbox" title="Show only properties with no structure on file">
                  <input
                    type="checkbox"
                    checked={vacantOnly}
                    onChange={(e) => onVacantOnlyChange(e.target.checked)}
                  />
                  <span>Vacant lots only</span>
                </label>
                {buyBox && onBuyBoxChange && (
                  <MapBuyBoxFields
                    value={buyBox}
                    onChange={onBuyBoxChange}
                    matchCount={buyBoxMatchCount}
                    stats={buyBoxStats}
                    saved={buyBoxSaved}
                  />
                )}
              </div>
            )}
          </div>

          {/* Export CSV — paid tiers only (Dashboard passes onExportCsv when
              isPro; this whole component only renders in the "all
              properties" view, so watchlist never sees it). County feed,
              ignores every filter above — see the menu items' own copy. */}
          {onExportCsv && (
            <div className="filter-export" ref={exportRef}>
              <button
                type="button"
                className="filter-more-btn"
                aria-expanded={exportOpen}
                aria-haspopup="true"
                disabled={exporting}
                onClick={() => setExportOpen((o) => !o)}
              >
                {exporting ? "Exporting…" : "Export CSV"}
              </button>
              {exportOpen && (
                <div className="filter-more-popover filter-export-popover" role="menu">
                  {EXPORT_RANGES.map((r) => (
                    <button
                      key={r.label}
                      type="button"
                      role="menuitem"
                      className="filter-export-option"
                      disabled={exporting}
                      onClick={() => handleExportClick(r.days)}
                    >
                      {r.label}
                    </button>
                  ))}
                  {exportError && (
                    <p className="filter-export-error" role="alert">
                      {exportError}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchAutocomplete;
